import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail } from "lucide-react";
import useReveal from "../lib/useReveal";
import Seo from "../components/Seo";
import { SERVICES, CLIENTS, CONTACT, HERO_IMAGE, CERTIFICATIONS } from "../lib/services";

// Bento grid column-span mapping for the 6-card services preview.
// Indexes 0 and 4 get a wider tile (4 cols); rest get 2 cols.
const WIDE_BENTO_INDEXES = new Set([0, 4]);
const bentoSpan = (index) =>
    WIDE_BENTO_INDEXES.has(index) ? "md:col-span-4" : "md:col-span-2";

export default function Home() {
    useReveal();

    return (
        <main data-testid="home-page">
            <Seo
                title="The Food Gala — Flavourful Perfection Delivered | B2B Catering Cuttack"
                description="Premium B2B catering for corporates, hospitals, events and parties across Cuttack, Bhubaneswar & Khorda. FSSAI · ISO 9001:2015 · Zed Silver certified. Trusted by Flipkart, IDBI Bank, JK University & more."
                path="/"
            />
            {/* HERO */}
            <section className="relative min-h-[100svh] overflow-hidden grain" data-testid="hero-section">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMAGE})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/55 via-[#1A1A1A]/35 to-[#1A1A1A]/80" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#1B4332]/30 via-transparent to-[#C84B31]/15" />

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-44 md:pt-52 pb-24">
                    <div className="grid md:grid-cols-12 gap-10 items-end min-h-[78svh]">
                        <div className="md:col-span-8 reveal">
                            <div className="flex items-center gap-3 mb-8">
                                <span className="h-px w-12 bg-[#FAF9F6]/70" />
                                <span className="text-[11px] uppercase tracking-[0.32em] text-[#FAF9F6]/80">
                                    B2B Catering · Est. for the discerning
                                </span>
                            </div>
                            <h1
                                className="font-display text-[2.6rem] leading-[1.02] md:text-[5.5rem] md:leading-[0.98] text-[#FAF9F6] tracking-tight"
                                data-testid="hero-headline"
                            >
                                Flavourful Perfection,
                                <br />
                                <em className="not-italic text-[#FFD9A8]">delivered</em> to make
                                <br />
                                workspace &amp; parties{" "}
                                <span className="italic text-[#E8EFEA]">memorable.</span>
                            </h1>
                            <p className="mt-8 max-w-xl text-[#FAF9F6]/85 text-lg font-light leading-relaxed">
                                Premium meal services for corporates, hospitals, institutions and personal
                                celebrations — crafted by culinary artisans, delivered on time, every time.
                            </p>

                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    to="/services"
                                    data-testid="hero-cta-services"
                                    className="inline-flex items-center gap-3 border border-[#FAF9F6]/40 text-[#FAF9F6] px-8 py-4 text-[12px] uppercase tracking-[0.26em] hover:bg-[#FAF9F6]/10 transition-all duration-300"
                                >
                                    Explore Services
                                </Link>
                            </div>
                        </div>

                        <div className="md:col-span-4 hidden md:block reveal">
                            <div className="border border-[#FAF9F6]/25 backdrop-blur-md bg-[#1A1A1A]/30 p-8">
                                <div className="text-[11px] uppercase tracking-[0.3em] text-[#FAF9F6]/70 mb-3">
                                    Per-plate · Bespoke
                                </div>
                                <div className="font-display text-3xl text-[#FAF9F6] leading-tight">
                                    From 50 to 5,000+ guests — tailored, certified, on time.
                                </div>
                                <div className="mt-7 grid grid-cols-3 gap-3" data-testid="hero-certifications">
                                    {CERTIFICATIONS.map((c) => (
                                        <div
                                            key={c.name}
                                            data-testid={`hero-cert-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                            className="border border-[#FAF9F6]/30 p-3 flex flex-col items-center text-center bg-[#FAF9F6]/5"
                                        >
                                            <span className="text-[9px] uppercase tracking-[0.22em] text-[#FAF9F6] leading-tight min-h-[24px]">
                                                {c.name}
                                            </span>
                                            <div className="mt-2 h-14 w-full rounded-sm bg-[#FAF9F6] grid place-items-center overflow-hidden">
                                                <img
                                                    src={c.image}
                                                    alt={c.full}
                                                    className="max-h-12 max-w-full object-contain"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE CLIENTS */}
            <section className="bg-[#FAF9F6] border-y border-black/5 py-8 overflow-hidden" data-testid="clients-marquee">
                <div className="flex items-center gap-16 marquee-track whitespace-nowrap will-change-transform">
                    {[...CLIENTS, ...CLIENTS, ...CLIENTS].map((c, i) => (
                        <span
                            key={`${c.name}-${i}`}
                            className="font-display italic text-2xl md:text-3xl text-[#1A1A1A]/55"
                        >
                            {c.name} <span className="mx-8 text-[#C84B31]">✦</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* INTRO */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="intro-section">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-6">
                            01 · The Food Gala
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight text-[#1A1A1A]">
                            More than meals.
                            <br />
                            <em className="text-[#1B4332] not-italic">Devoted partners</em> in
                            every plate.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 reveal">
                        <p className="text-lg leading-relaxed text-[#1A1A1A]/75 font-light">
                            At The Food Gala, we transcend being mere meal service providers. We
                            become your devoted partners — delivering delectable and nourishing meals
                            to corporates, hospitals, senior citizens, students, and personal
                            celebrations. Our steadfast dedication to quality, customization, and
                            genuine care ensures culinary excellence arrives right at your doorstep.
                        </p>
                        <div className="mt-10 flex items-center gap-4">
                            <Link to="/about" data-testid="intro-cta-about" className="link-underline text-[12px] uppercase tracking-[0.24em] text-[#1B4332]">
                                Read our story →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW — BENTO */}
            <section className="bg-[#F3F1EC] py-24 md:py-32" data-testid="services-preview">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
                        <div className="reveal">
                            <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-4">
                                02 · Meal Services
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
                                Eight ways we feed the day.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            data-testid="services-view-all"
                            className="text-[12px] uppercase tracking-[0.26em] text-[#1B4332] link-underline"
                        >
                            See all services →
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
                        {SERVICES.slice(0, 6).map((s, i) => {
                            const span = bentoSpan(i);
                            return (
                                <Link
                                    key={s.slug}
                                    to={`/services#${s.slug}`}
                                    data-testid={`service-card-${s.slug}`}
                                    className={`${span} group relative zoom-wrap aspect-[5/4] md:aspect-auto md:h-[340px] block bg-[#1B4332] reveal`}
                                >
                                    <div
                                        className="absolute inset-0 bg-cover bg-center zoom"
                                        style={{ backgroundImage: `url(${s.image})` }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/20 to-transparent" />
                                    <div className="relative h-full flex flex-col justify-between p-6 md:p-8 text-[#FAF9F6]">
                                        <span className="text-[10px] uppercase tracking-[0.32em] bg-[#FAF9F6]/15 backdrop-blur-sm border border-[#FAF9F6]/25 px-3 py-1 self-start">
                                            {s.kind}
                                        </span>
                                        <div>
                                            <div className="font-display text-2xl md:text-3xl leading-tight">
                                                {s.name}
                                            </div>
                                            <div className="mt-2 text-[13px] text-[#FAF9F6]/85 max-w-md">
                                                {s.short}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* QUOTE / TAGLINE STRIP */}
            <section className="relative py-20 md:py-28 overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(/brochure/food6.jpg)" }}
                />
                <div className="absolute inset-0 bg-[#1B4332]/82" />
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center reveal">
                    <span
                        className="inline-block bg-[#FAF9F6] text-[#1B4332] font-bold uppercase tracking-[0.32em] text-[11px] md:text-[12px] px-5 py-2.5 mb-8"
                        data-testid="home-quote-caption"
                    >
                        Crafting Memories &amp; Nourishing Lives
                    </span>
                    <h3
                        className="font-display font-bold text-3xl md:text-6xl leading-[1.05] text-[#ECC97A]"
                        data-testid="home-quote-text"
                    >
                        "We believe in the transformative power of food to craft memories, and bring
                        joy, health &amp; satisfaction."
                    </h3>
                    <div className="mt-10">
                        <Link
                            to="/enquiry"
                            data-testid="quote-cta-enquire"
                            className="inline-flex items-center gap-3 bg-[#C84B31] text-[#FAF9F6] px-8 py-4 text-[12px] uppercase tracking-[0.26em] hover:bg-[#a13a24] hover:-translate-y-[2px] transition-all duration-300"
                        >
                            Have a Gala Experience
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CONTACT STRIP */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="home-contact-section">
                <div className="grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-4">
                            03 · Be in touch
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
                            Let's plan something
                            <br /> <em className="not-italic text-[#1B4332]">truly delicious.</em>
                        </h2>
                        <p className="mt-6 text-lg text-[#1A1A1A]/75 max-w-lg font-light">
                            Tell us about your team, your event or your institution. We'll design a
                            customised meal plan and reach back within one business day.
                        </p>
                    </div>
                    <div className="md:col-span-6 reveal">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <a
                                href={`tel:${CONTACT.phone}`}
                                data-testid="home-contact-phone"
                                className="group border border-black/10 p-8 bg-[#FAF9F6] hover:border-[#1B4332] transition-colors"
                            >
                                <Phone size={20} className="text-[#C84B31]" />
                                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/60">Call</div>
                                <div className="mt-1 font-display text-2xl">{CONTACT.phone}</div>
                            </a>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="home-contact-email"
                                className="group border border-black/10 p-8 bg-[#FAF9F6] hover:border-[#1B4332] transition-colors"
                            >
                                <Mail size={20} className="text-[#C84B31]" />
                                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/60">Write</div>
                                <div className="mt-1 font-display text-2xl break-all">{CONTACT.email}</div>
                            </a>
                            <div className="sm:col-span-2 border border-black/10 p-8 bg-[#FAF9F6]">
                                <div className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/60">Visit</div>
                                <div className="mt-1 font-display text-2xl">{CONTACT.address}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
